import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

/**
 * Scroll-reveal: returns a ref to attach to an element and whether it has
 * entered the viewport yet. Elements start hidden (via the `.reveal` class) and
 * fade/slide in once seen. Progressive enhancement — if IntersectionObserver is
 * unavailable, content is revealed immediately so nothing is ever stranded.
 * `prefers-reduced-motion` is handled in CSS (the hidden state is disabled).
 */
export const useReveal = <T extends HTMLElement = HTMLDivElement>(): {
  ref: RefObject<T>;
  inView: boolean;
} => {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (!('IntersectionObserver' in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setInView(true);
            io.unobserve(e.target);
          }
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return { ref, inView };
};
